import { useEffect, useMemo, useState } from 'react';

import { useAuthContext } from '@/features/auth/AuthContext';
import { useT } from '@/i18n/LocaleProvider';

import type { WatchedPage } from './api';
import { createWatchApiClient } from './api';

export type WatchedPagesProps = {
  onNavigate?: (slug: string, lang: string) => void;
};

export function WatchedPages({ onNavigate }: WatchedPagesProps) {
  const t = useT();
  const auth = useAuthContext();
  const [watches, setWatches] = useState<WatchedPage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(() => createWatchApiClient(() => auth.token), [auth.token]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(null);

    client.listWatches().then(
      (resp) => {
        if (cancelled) return;
        setWatches(resp.watches);
        setLoading(false);
      },
      () => {
        if (cancelled) return;
        setError(t('watchlist_error_load'));
        setLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, client, t]);

  return (
    <article className="note" data-testid="watched-pages">
      <header className="note-header">
        <h1>{t('watchlist_title')}</h1>
      </header>

      {loading ? <div className="empty-note">{t('watchlist_loading')}</div> : null}
      {!loading && error ? <div className="empty-note">{error}</div> : null}
      {!loading && !error && watches.length === 0 ? (
        <div className="empty-note">{t('watchlist_empty')}</div>
      ) : null}

      {!loading && !error && watches.length > 0 ? (
        <ul className="page-index-list">
          {watches.map((w) => (
            <li key={w.page_id}>
              {onNavigate && w.slug && w.language ? (
                <a
                  href={`/${w.language}/pages/${encodeURIComponent(w.slug)}`}
                  onClick={(event) => {
                    event.preventDefault();
                    if (w.slug && w.language) onNavigate(w.slug, w.language);
                  }}
                >
                  <strong>{w.title ?? w.slug ?? w.page_id}</strong>
                </a>
              ) : (
                <span>{w.title ?? w.slug ?? w.page_id}</span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}
